// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject, TemplateRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { TEMPLATE_IDENTIFIERS } from '@lfx/key-projects/key-projects.model';

/**
 * Meetup attendance data model from SQL query (API returns uppercase keys, transformed to camelCase)
 */
export interface MeetupAttendanceData {
  USER_ID: string;
  USER_FULL_NAME: string;
  USER_EMAIL: string;
  USER_PHOTO_URL?: string;
  USER_TITLE?: string;
  EVENT_NAME: string;
  EVENT_START_DATE: string;
  EVENT_CITY?: string;
  EVENT_COUNTRY?: string;
  EVENT_URL?: string;
  SOURCE: string;
  PROJECT_ID: string;
  PROJECT_NAME: string;
  PROJECT_SLUG: string;
}

/**
 * Meetup attendance summary data model
 */
export interface MeetupAttendanceSummaryData {
  totalMeetups: number;
}

/**
 * Entity service for meetup attendance metric type
 */
@Injectable({
  providedIn: 'root'
})
export class MeetupAttendanceMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  private readonly config = {
    definition: "Count of events for this project's foundation's non-LF meetups",
    columns: [
      { name: 'Our meetup attenders', prop: 'displayName', templateId: TEMPLATE_IDENTIFIERS.USER_PROFILE },
      { name: 'Meetup name', prop: 'meetupName' },
      { name: 'Meetup date', prop: 'meetupDate' }
    ],
    summaryColumns: [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total count for this project', prop: 'totalCount' },
      { name: 'Data source', prop: 'dataSource' }
    ],
    dataSource: 'Bevy, Regfox'
  };

  /**
   * Get columns for meetup attendance table
   */
  getColumns(): Column[] {
    return this.config.columns.map(col => {
      const { templateId, ...columnProps } = col;
      return {
        ...columnProps,
        ...(templateId && { template: templateId as unknown as TemplateRef<unknown> })
      };
    });
  }

  /**
   * Get API endpoint for meetup attendance
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/meetup-attendance`;
  }

  /**
   * Fetch meetup attendance data from backend
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<MeetupAttendanceData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch meetup attendance data');
            }

            return response.data.map(attendance => this.transformToDataRow(attendance));
          }),
          catchError(error => {
            console.error('Error fetching meetup attendance data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in meetup attendance entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform meetup attendance data to DataRow format for the table
   */
  private transformToDataRow(attendance: MeetupAttendanceData): DataRow {
    return {
      displayName: {
        text: attendance.USER_FULL_NAME,
        extraData: {
          imageUrl: attendance.USER_PHOTO_URL,
          userId: attendance.USER_ID,
          memberDisplayName: attendance.USER_FULL_NAME,
          memberLogoUrl: attendance.USER_PHOTO_URL
        }
      },
      meetupName: { text: attendance.EVENT_NAME },
      meetupDate: { text: this.formatDate(attendance.EVENT_START_DATE) }
    };
  }

  /**
   * Fetch meetup attendance summary data from backend
   * Summary data comes from ECOSYSTEM_INFLUENCE table (TOTAL_MEETUP_ATTENDANCE field)
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: MeetupAttendanceSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch meetup attendance summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = this.config.summaryColumns;

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: response.data.totalMeetups.toLocaleString('en-US') },
                dataSource: { text: this.config.dataSource }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching meetup attendance summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = this.config.summaryColumns;
            const fallbackData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: 'N/A' },
                dataSource: { text: this.config.dataSource }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in meetup attendance summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = this.config.summaryColumns;
        const fallbackData: DataRow[] = [
          {
            definition: { text: this.config.definition },
            totalCount: { text: 'N/A' },
            dataSource: { text: this.config.dataSource }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}

